import type { GridApi } from 'ag-grid-community'

import type { CombinedListingSearchResult } from '@/queries/CombinedListingSearchResult'

import { ColumnIdEnum } from '../types'

/**
 * Bid Sheet Upload Tool (GLOB-4879, BP-2572) — these columns are hidden
 * on-screen (export-only, see useLayoutColumnDefs.ts) but must still appear
 * in the right-click CSV/Excel Export, which otherwise only exports visible
 * columns. Force-include them by colId regardless of hidden state, without
 * widening this to every other hidden column (unlike the toolbar Export
 * button, which already exports everything via allColumns:true).
 *
 * Sourced from ColumnIdEnum (not a standalone string literal array) so a
 * rename of any of these colIds in useColumnDefs.ts fails to compile here
 * instead of silently drifting out of sync.
 */
const BID_SHEET_COLUMN_IDS: ColumnIdEnum[] = [
  ColumnIdEnum.listingId,
  ColumnIdEnum.minRequiredBid,
  ColumnIdEnum.bidSubmission,
]

/**
 * AG Grid's ColDef has no per-column CSV/Excel export suppression — the only
 * real mechanism is an explicit `columnKeys` allow-list — so every column-key
 * computation in this file (and ExportListViewButton.tsx's toolbar export)
 * must filter these out itself. The bulk-select checkbox column is UI-only,
 * as is the action column (bid form / action menu controls, see
 * useColumnDefs.ts) — both have `lockVisible` so they always reach the
 * export, and their valueGetters return the SENTINEL placeholder or grid-only
 * state rather than exportable data.
 */
const EXCLUDED_FROM_EXPORT_COLUMN_IDS = new Set<string>([
  ColumnIdEnum.checkbox,
  ColumnIdEnum.action,
])

export const getCsvExportColumnKeys = (
  api: GridApi<CombinedListingSearchResult>
): string[] => {
  const displayedColIds = api
    .getAllDisplayedColumns()
    .map((column) => column.getColId())
    .filter((colId) => !EXCLUDED_FROM_EXPORT_COLUMN_IDS.has(colId))
  const displayedColIdSet = new Set(displayedColIds)
  const gridColIds = new Set(
    api.getAllGridColumns().map((column) => column.getColId())
  )
  // Skip a Bid Sheet column already displayed (e.g. manually un-hidden by
  // the user) — it's already in displayedColIds, so appending it again would
  // duplicate that column in the export.
  const bidSheetColIds = BID_SHEET_COLUMN_IDS.filter(
    (id) => gridColIds.has(id) && !displayedColIdSet.has(id)
  )
  return [...displayedColIds, ...bidSheetColIds]
}

/**
 * All grid columns, including hidden ones — for the toolbar Export button,
 * which exports every column regardless of visibility (see
 * ExportListViewButton.tsx's doc comment) — minus
 * EXCLUDED_FROM_EXPORT_COLUMN_IDS.
 */
export const getAllExportColumnKeys = (
  api: GridApi<CombinedListingSearchResult>
): string[] =>
  api
    .getAllGridColumns()
    .map((column) => column.getColId())
    .filter((colId) => !EXCLUDED_FROM_EXPORT_COLUMN_IDS.has(colId))
